/**
 * Evaluation video ↔ sequence ↔ player matching for exercise results Media tab.
 */

import {
  DEFAULT_CAMERA_SETUP,
  cameraOrdinalFromViewSetup,
  cameraSetupFromViewSetup,
  formatCameraSetupLabel,
  orientedPlayerFromSetup,
  parseCameraSetupFromFilename,
  resolveCameraSetup,
} from "./evaluationCameraSetup";

const SEQUENCE_FILENAME_RE = /_seq(\d+)/i;
const SEQUENCE_TE_ID_SUFFIX_RE = /_seq(\d+)_([0-9A-Fa-f]{8})$/;
const TRAINING_EXERCISE_URL_TE_ID_RE = /training_exercise_(te\d+)_seq\d+_/i;

function parsePositiveInt(value) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function parseTeIdFromVideoUrl(url) {
  if (!url) return null;
  const match = String(url).match(TRAINING_EXERCISE_URL_TE_ID_RE);
  return match ? match[1] : null;
}

/** Coach Move sets view_setup.te_id_manual so DB te_id wins over the filename. */
export function isManualTeAssignment(fb) {
  const setup = parseFeedbackViewSetup(fb);
  return setup?.te_id_manual === true || setup?.assignment_manual === true;
}

function parseFeedbackViewSetup(fb) {
  const raw = fb?.view_setup;
  if (raw == null || raw === "") return null;
  try {
    const setup = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!setup || typeof setup !== "object" || Array.isArray(setup)) return null;
    return setup;
  } catch {
    return null;
  }
}

function normalizeAssignedPlayers(raw) {
  if (!Array.isArray(raw) || !raw.length) return [];
  const bySlot = new Map();
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const slot = String(entry.position || entry.slot || "")
      .trim()
      .toUpperCase();
    if (!["A", "B", "C", "D"].includes(slot) || bySlot.has(slot)) continue;
    bySlot.set(slot, {
      slot,
      playerId: entry.player_id ?? entry.playerId ?? null,
      userId: entry.user_id ?? entry.userId ?? null,
      displayName: entry.display_name ?? entry.displayName ?? null,
      role: entry.role ?? null,
    });
  }
  const ordered = ["A", "B", "C", "D"]
    .map((slot) => bySlot.get(slot))
    .filter(Boolean);
  // Same person stamped on A and B (or player_id + user_id mix) → one line.
  const seenIds = new Set();
  const unique = [];
  for (const entry of ordered) {
    const ids = [entry.playerId, entry.userId].filter(Boolean).map(String);
    if (ids.length && ids.some((id) => seenIds.has(id))) continue;
    ids.forEach((id) => seenIds.add(id));
    unique.push(entry);
  }
  return unique;
}

/**
 * Players stamped on the clip at record/upload time (view_setup.assigned_players),
 * even when the coach has not run Move (no assignment_manual flag).
 */
export function resolveClipAssignedPlayers(msg) {
  const setup = parseFeedbackViewSetup(msg);
  return normalizeAssignedPlayers(setup?.assigned_players);
}

/** Manual Move assignment fields (sequence / table / camera) from view_setup. */
export function resolveManualVideoAssignment(fb) {
  const setup = parseFeedbackViewSetup(fb);
  if (!setup || !(setup.te_id_manual === true || setup.assignment_manual === true)) {
    return null;
  }
  const positions = Array.isArray(setup.positions)
    ? setup.positions.map((p) => String(p || "").trim().toUpperCase()).filter(Boolean)
    : [];
  const camera =
    (setup.camera_position && String(setup.camera_position).trim().toUpperCase()) ||
    positions[0] ||
    null;
  const sequenceNumber = parsePositiveInt(setup.sequence_number);
  const tableNumber = parsePositiveInt(setup.table_number);
  const sharedUserIds = Array.isArray(setup.shared_user_ids)
    ? setup.shared_user_ids.map((id) => String(id)).filter(Boolean)
    : [];
  const cameraSetup =
    setup.camera_setup ||
    setup.cameraSetup ||
    null;
  const cameraOrdinal = (() => {
    const n = Number(setup.camera_ordinal ?? setup.cameraOrdinal);
    return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
  })();
  return {
    sequenceNumber,
    tableNumber,
    positions,
    cameraPosition:
      camera && ["A", "B", "C", "D"].includes(camera)
        ? camera === "C"
          ? "A"
          : camera === "D"
            ? "B"
            : camera
        : null,
    cameraSetup,
    cameraOrdinal,
    sharedUserIds,
    assignedPlayers: normalizeAssignedPlayers(setup.assigned_players),
  };
}

/**
 * Wall-clock recording time for Media cards.
 * Prefers view_setup.recorded_at (set on upload / preserved on Move) over DB createdAt.
 */
export function resolveEvaluationVideoRecordingDate(msg) {
  const setup = parseFeedbackViewSetup(msg);
  const candidates = [setup?.recorded_at, msg?.recorded_at, msg?.createdAt];
  for (const raw of candidates) {
    if (raw == null || raw === "") continue;
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return null;
}

function rosterSlotLetter(player) {
  const pos = String(player?.player_position || "")
    .replace(/\s+/g, "")
    .toLowerCase();
  if (pos === "player1" || pos === "a") return "A";
  if (pos === "player2" || pos === "b") return "B";
  if (pos === "player3" || pos === "c") return "C";
  if (pos === "player4" || pos === "d") return "D";
  return null;
}

/**
 * Players shown on a Media card after coach Move — last manual assignment only.
 * Never dumps every roster row on the table (that produced duplicate A/B lines).
 */
export function resolveManualAssignmentPlayers({
  msg,
  assignment = null,
  exerciseContext = null,
  seq = null,
} = {}) {
  const resolved = assignment || resolveManualVideoAssignment(msg);
  if (!resolved) return null;

  if (resolved.assignedPlayers?.length) {
    return resolved.assignedPlayers;
  }

  const roster = Array.isArray(exerciseContext?.players)
    ? exerciseContext.players
    : [];
  const table = Number(resolved.tableNumber);
  const sharedIds = (resolved.sharedUserIds || []).map(String);
  const senderId = msg?.sender_id != null ? String(msg.sender_id) : null;
  const identityIds = [...new Set([...sharedIds, senderId].filter(Boolean))];

  const candidates = [];
  if (seq) {
    const seqSlots = [
      { slot: "A", playerId: seq.player_a_id, role: seq.player_a_role },
      { slot: "B", playerId: seq.player_b_id, role: seq.player_b_role },
      { slot: "C", playerId: seq.player_c_id, role: seq.player_c_role },
      { slot: "D", playerId: seq.player_d_id, role: seq.player_d_role },
    ];
    for (const entry of seqSlots) {
      if (!entry.playerId) continue;
      const player = roster.find((p) => playerLikeMatchesIds(p, [String(entry.playerId)]));
      candidates.push({
        slot: entry.slot,
        playerId: entry.playerId,
        userId: player?.user_id ?? null,
        displayName: null,
        role: entry.role || null,
        tableNumber: Number(player?.table_number) || null,
      });
    }
  }

  for (const player of roster) {
    const slot = rosterSlotLetter(player);
    if (!slot) continue;
    if (Number.isFinite(table) && table > 0 && Number(player.table_number) !== table) {
      continue;
    }
    candidates.push({
      slot,
      playerId: player.player_id ?? null,
      userId: player.user_id ?? null,
      displayName: null,
      role: null,
      tableNumber: Number(player.table_number) || null,
      player,
    });
  }

  const matchIdentity = (cand) => {
    if (!identityIds.length) return true;
    return identityIds.some(
      (id) =>
        (cand.playerId != null && String(cand.playerId) === id) ||
        (cand.userId != null && String(cand.userId) === id) ||
        (cand.player && playerLikeMatchesIds(cand.player, [id]))
    );
  };

  const positions = (resolved.positions || []).filter((p) =>
    ["A", "B", "C", "D"].includes(p)
  );
  // Prefer explicitly shared identities; fall back to selected court sides.
  const wantSlots = positions.length
    ? positions
    : identityIds.length
      ? ["A", "B", "C", "D"]
      : ["A", "B"];

  const bySlot = new Map();
  for (const slot of wantSlots) {
    const matches = candidates.filter(
      (c) => c.slot === slot && matchIdentity(c)
    );
    const pick =
      matches.find((c) =>
        identityIds.some(
          (id) =>
            (c.userId != null && String(c.userId) === id) ||
            (c.playerId != null && String(c.playerId) === id)
        )
      ) || matches[0];
    if (pick && !bySlot.has(slot)) {
      bySlot.set(slot, {
        slot,
        playerId: pick.playerId,
        userId: pick.userId,
        displayName: pick.displayName,
        role: pick.role,
      });
    }
  }

  // If only camera-side was shared, still show the same-table partner once.
  if (bySlot.size === 1 && identityIds.length) {
    const only = [...bySlot.values()][0];
    const onlyIds = [only.playerId, only.userId].filter(Boolean).map(String);
    const partnerSlot =
      only.slot === "A" ? "B" : only.slot === "B" ? "A" : null;
    if (partnerSlot) {
      const partner = candidates.find((c) => {
        if (c.slot !== partnerSlot) return false;
        // Never list the same person twice as A and B.
        const partnerIds = [c.playerId, c.userId].filter(Boolean).map(String);
        if (partnerIds.some((id) => onlyIds.includes(id))) return false;
        if (Number.isFinite(table) && table > 0) {
          return Number(c.tableNumber) === table || c.tableNumber == null;
        }
        return true;
      });
      if (partner) {
        bySlot.set(partnerSlot, {
          slot: partnerSlot,
          playerId: partner.playerId,
          userId: partner.userId,
          displayName: partner.displayName,
          role: partner.role,
        });
      }
    }
  }

  const result = ["A", "B", "C", "D"]
    .map((slot) => bySlot.get(slot))
    .filter(Boolean);
  // Drop duplicate identities that landed on multiple slots (bad sequence /
  // roster data), keeping the first court side.
  const seenIds = new Set();
  const unique = [];
  for (const entry of result) {
    const ids = [entry.playerId, entry.userId].filter(Boolean).map(String);
    if (ids.length && ids.some((id) => seenIds.has(id))) continue;
    ids.forEach((id) => seenIds.add(id));
    unique.push(entry);
  }
  return unique.length ? unique : null;
}

/** Prefer te_id embedded in clip URL over the DB row when present — unless coach moved it. */
export function feedbackVideoBelongsToTe(fb, teId) {
  const target = String(teId);
  if (!fb) return false;
  if (!fb.video_message_url || isManualTeAssignment(fb)) {
    return String(fb.te_id) === target;
  }
  const urlTe = parseTeIdFromVideoUrl(fb.video_message_url);
  if (urlTe) return urlTe === target;
  return String(fb.te_id) === target;
}

/**
 * Table from a coach Move assignment.
 * A manual move must not fall back to the filename table — service clips
 * recorded on another station keep SmartTT_T{n}_ in the URL.
 */
export function resolveFeedbackTableNumber(fb) {
  const manual = resolveManualVideoAssignment(fb);
  if (manual) return manual.tableNumber || null;
  return parseTableNumberFromVideoUrl(fb?.video_message_url);
}

/**
 * Tables a clip was filmed for.
 * A coach Move keeps a single table. A wide table-phone calibration lists
 * every selected table in view_setup.table_numbers.
 */
export function clipFilmedTableNumbers(msg) {
  if (isManualTeAssignment(msg)) {
    const moved = resolveFeedbackTableNumber(msg);
    return moved ? [moved] : [];
  }
  const setup = parseFeedbackViewSetup(msg);
  const nums = [];
  if (Array.isArray(setup?.table_numbers)) {
    for (const raw of setup.table_numbers) {
      const n = parsePositiveInt(raw);
      if (n) nums.push(n);
    }
  }
  const fromUrl = parseTableNumberFromVideoUrl(
    msg?.video_message_url || msg?.video_url
  );
  if (fromUrl) nums.push(fromUrl);
  return [...new Set(nums)];
}

/** Player and user ids stored on a coach Move assignment. */
function manualAssignedIdentityIds(msg) {
  if (!isManualTeAssignment(msg)) return [];
  const setup = parseFeedbackViewSetup(msg);
  const ids = [];
  for (const entry of setup?.assigned_players || []) {
    if (entry?.user_id != null) ids.push(String(entry.user_id));
    if (entry?.player_id != null) ids.push(String(entry.player_id));
    if (entry?.userId != null) ids.push(String(entry.userId));
    if (entry?.playerId != null) ids.push(String(entry.playerId));
  }
  return ids;
}

/** True when the viewer is rostered on this table (multi-table TE). */
export function viewerIsOnExerciseTable(viewerIds, tableNumber, exerciseContext) {
  const table = Number(tableNumber);
  if (!Number.isFinite(table) || table <= 0) return true;
  if (!viewerIds?.length) return true;

  const roster = Array.isArray(exerciseContext?.players)
    ? exerciseContext.players
    : [];
  const onTable = roster.filter((p) => Number(p.table_number) === table);
  // No per-table roster → cannot enforce; allow sequence-role match.
  if (!onTable.length) return true;
  return onTable.some((p) => playerLikeMatchesIds(p, viewerIds));
}

/**
 * When an exercise has sequence rows, reject clips whose sequence_number
 * does not exist on that exercise (e.g. ex1 seq7 misfiled under ex2).
 * Clips without a resolvable sequence order stay allowed (TE_ONCE / timer).
 */
export function feedbackSequencePlausibleForExercise(fb, sequences = []) {
  if (!Array.isArray(sequences) || sequences.length === 0) return true;
  const order = resolveFeedbackSequenceOrder(fb);
  if (order == null) return true;
  return sequences.some(
    (seq) => Number(seq.sequence_order) === Number(order)
  );
}

function normalizeEvaluationVideoUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  let path = raw.split("?")[0].split("#")[0].trim();
  try {
    if (/^https?:\/\//i.test(path)) {
      path = new URL(path).pathname || path;
    }
  } catch {
    /* keep path */
  }
  return path.replace(/^\/+/, "").toLowerCase();
}

function evaluationVideoDedupeRank(msg) {
  let score = 0;
  if (isManualTeAssignment(msg)) score += 1000;
  if (Number(msg?.sequence_number) > 0) score += 100;
  const updated = msg?.updatedAt ? new Date(msg.updatedAt).getTime() : 0;
  const created = msg?.createdAt ? new Date(msg.createdAt).getTime() : 0;
  return score + Math.max(updated, created) / 1e15;
}

/** One card per physical clip — dual-active uploads may create multiple DB rows for the same URL. */
export function dedupeEvaluationVideosByUrl(videos, { preferredTeId } = {}) {
  if (!Array.isArray(videos) || videos.length < 2) return videos || [];

  const bestByUrl = new Map();
  for (const msg of videos) {
    const url = normalizeEvaluationVideoUrl(msg?.video_message_url);
    if (!url) continue;
    let score = evaluationVideoDedupeRank(msg);
    if (preferredTeId && String(msg?.te_id) === String(preferredTeId)) {
      score += 1000;
    }
    const existing = bestByUrl.get(url);
    if (!existing || score > existing.score) {
      bestByUrl.set(url, { msg, score });
    }
  }

  if (!bestByUrl.size) return videos;

  const seen = new Set();
  const result = [];
  for (const msg of videos) {
    const url = normalizeEvaluationVideoUrl(msg?.video_message_url);
    if (!url) {
      result.push(msg);
      continue;
    }
    const best = bestByUrl.get(url)?.msg;
    if (best !== msg || seen.has(url)) continue;
    seen.add(url);
    result.push(msg);
  }
  return result;
}

export function parseSequenceOrderFromTeId(teId) {
  if (!teId) return null;
  const match = String(teId).trim().match(SEQUENCE_TE_ID_SUFFIX_RE);
  if (!match) return null;
  return parsePositiveInt(match[1]);
}

export function parseSequenceOrderFromFilename(name) {
  if (!name) return null;
  const raw = String(name).trim();
  if (!raw) return null;

  const fromTe = parseSequenceOrderFromTeId(raw);
  if (fromTe) return fromTe;

  const seqMatch = raw.match(SEQUENCE_FILENAME_RE);
  if (seqMatch) return parsePositiveInt(seqMatch[1]);

  return null;
}

/** iOS export embeds table + court side / setup: SmartTT_T1_A_seq2_… or _specA_ / _refB_ */
const CAMERA_POSITION_FILENAME_RE = /SmartTT_T\d+_([ABCD])_/i;
const CAMERA_POSITION_GENERIC_RE = /_([ABCD])_seq\d+/i;
const TABLE_NUMBER_FILENAME_RE = /SmartTT_T(\d+)_/i;
function videoFilenameFromUrl(url) {
  if (!url) return "";
  const path = String(url).split("?")[0];
  try {
    return decodeURIComponent(path.split("/").pop() || path);
  } catch {
    return path.split("/").pop() || path;
  }
}

/**
 * Parse physical table number from an evaluation video URL or filename.
 */
export function parseTableNumberFromVideoUrl(url) {
  const name = videoFilenameFromUrl(url);
  const match = name.match(TABLE_NUMBER_FILENAME_RE);
  return match ? parsePositiveInt(match[1]) : null;
}

/**
 * Parse camera court position (A–B) from an evaluation video URL or filename.
 * Legacy C/D map to A/B. Prefer setup tokens (specA/refB) when present.
 */
export function parseCameraPositionFromVideoUrl(url) {
  if (!url) return null;
  const name = videoFilenameFromUrl(url);
  const setup = parseCameraSetupFromFilename(name);
  if (setup) return orientedPlayerFromSetup(setup);
  const ios = name.match(CAMERA_POSITION_FILENAME_RE);
  if (ios) {
    const letter = ios[1].toUpperCase();
    if (letter === "C") return "A";
    if (letter === "D") return "B";
    return letter;
  }
  const generic = name.match(CAMERA_POSITION_GENERIC_RE);
  if (generic) {
    const letter = generic[1].toUpperCase();
    if (letter === "C") return "A";
    if (letter === "D") return "B";
    return letter;
  }
  return null;
}

const CAMERA_SETUP_ORDER = {
  spectator_a: 0,
  spectator_b: 1,
  referee_a: 2,
  referee_b: 3,
};
const CAMERA_POSITION_ORDER = { A: 0, B: 1, C: 0, D: 1 };

/**
 * Resolve canonical camera_setup for a clip.
 */
export function resolveEvaluationVideoCameraSetup(msg, seq = null, resolveSlotIds = null) {
  const setup = parseFeedbackViewSetup(msg);
  const fromSetup = cameraSetupFromViewSetup(setup);
  if (fromSetup) return fromSetup;

  const fromUrl = parseCameraSetupFromFilename(
    videoFilenameFromUrl(msg?.video_message_url)
  );
  if (fromUrl) return fromUrl;

  const legacyPos = resolveEvaluationVideoCameraPosition(msg, seq, resolveSlotIds);
  return (
    resolveCameraSetup({ cameraPosition: legacyPos }) || DEFAULT_CAMERA_SETUP
  );
}

export function resolveEvaluationVideoCameraOrdinal(msg) {
  return cameraOrdinalFromViewSetup(msg?.view_setup) || 1;
}

/**
 * Resolve camera position A–B for a clip: setup / filename first, then sender ↔ sequence slot.
 * `resolveSlotIds(position)` should return identity strings (player_id / user_id) for that slot.
 */
export function resolveEvaluationVideoCameraPosition(
  msg,
  seq,
  resolveSlotIds = null
) {
  const manual = resolveManualVideoAssignment(msg);
  if (manual?.cameraPosition) {
    const p = manual.cameraPosition;
    return p === "C" ? "A" : p === "D" ? "B" : p;
  }

  const fromSetup = cameraSetupFromViewSetup(msg?.view_setup);
  if (fromSetup) return orientedPlayerFromSetup(fromSetup);

  const fromUrl = parseCameraPositionFromVideoUrl(msg?.video_message_url);
  if (fromUrl) return fromUrl;

  const sender = msg?.sender_id != null ? String(msg.sender_id) : null;
  if (!sender || !seq || typeof resolveSlotIds !== "function") return null;

  for (const position of ["A", "B", "C", "D"]) {
    const ids = resolveSlotIds(seq, position) || [];
    if (ids.some((id) => id != null && String(id) === sender)) {
      if (position === "C") return "A";
      if (position === "D") return "B";
      return position;
    }
  }
  return null;
}

/**
 * Collect distinct evaluation clips for a sequence (all camera angles the viewer can access).
 * Does not collapse dual-active shared uploads to a single clip when URLs differ.
 */
export function collectSequenceEvaluationVideos(
  seq,
  videoLists,
  viewerIds,
  sequences = [],
  exerciseContext = null
) {
  if (!seq) return [];
  const order =
    seq.sequence_order != null && Number.isFinite(Number(seq.sequence_order))
      ? Number(seq.sequence_order)
      : null;
  const tesId = seq.tes_id != null ? String(seq.tes_id) : null;
  const isSyntheticOnce = tesId != null && String(tesId).startsWith("once-");

  const seen = new Set();
  const result = [];

  for (const list of videoLists || []) {
    if (!Array.isArray(list)) continue;
    for (const msg of list) {
      if (!msg?.video_message_url || msg.message_type !== "evaluation") continue;

      const msgOrder = resolveFeedbackSequenceOrder(msg);
      if (order != null && msgOrder != null && Number(msgOrder) !== order) {
        continue;
      }
      if (
        order != null &&
        msgOrder == null &&
        sequences.length > 1 &&
        !isSyntheticOnce
      ) {
        continue;
      }

      if (
        !evaluationFeedbackMatchesViewer(msg, viewerIds, sequences, {
          targetSequenceOrder: isSyntheticOnce ? null : order,
          exerciseContext,
        })
      ) {
        continue;
      }

      const urlKey = normalizeEvaluationVideoUrl(msg.video_message_url);
      if (!urlKey || seen.has(urlKey)) continue;
      seen.add(urlKey);
      result.push(msg);
    }
  }

  return result;
}

export function sortCameraVideoOptions(options) {
  return [...(options || [])].sort((a, b) => {
    const as =
      a.cameraSetup && CAMERA_SETUP_ORDER[a.cameraSetup] != null
        ? CAMERA_SETUP_ORDER[a.cameraSetup]
        : 99;
    const bs =
      b.cameraSetup && CAMERA_SETUP_ORDER[b.cameraSetup] != null
        ? CAMERA_SETUP_ORDER[b.cameraSetup]
        : 99;
    if (as !== bs) return as - bs;
    const ao = Number(a.cameraOrdinal) || 1;
    const bo = Number(b.cameraOrdinal) || 1;
    if (ao !== bo) return ao - bo;
    const ap =
      a.cameraPosition && CAMERA_POSITION_ORDER[a.cameraPosition] != null
        ? CAMERA_POSITION_ORDER[a.cameraPosition]
        : 99;
    const bp =
      b.cameraPosition && CAMERA_POSITION_ORDER[b.cameraPosition] != null
        ? CAMERA_POSITION_ORDER[b.cameraPosition]
        : 99;
    if (ap !== bp) return ap - bp;
    return String(a.url || "").localeCompare(String(b.url || ""));
  });
}

export function formatEvaluationCameraOptionLabel(option, t, optionIdx = 0) {
  if (option?.cameraSetupLabel) return option.cameraSetupLabel;
  if (option?.cameraSetup) {
    return formatCameraSetupLabel(
      option.cameraSetup,
      option.cameraOrdinal,
      t
    );
  }
  const position = option?.cameraPosition;
  if (position) {
    return (
      t?.("cameraPosition", "Camera {{position}}", { position }) ||
      `Camera ${position}`
    );
  }
  return (
    t?.("cameraPositionUnknown", "Camera {{n}}", { n: optionIdx + 1 }) ||
    `Camera ${optionIdx + 1}`
  );
}

export function resolveFeedbackSequenceOrder(msg) {
  if (!msg) return null;
  const manual = resolveManualVideoAssignment(msg);
  if (manual?.sequenceNumber) return manual.sequenceNumber;
  const fromColumn = parsePositiveInt(msg.sequence_number);
  if (fromColumn) return fromColumn;
  const fromInferred = parsePositiveInt(msg.inferred_sequence_number);
  if (fromInferred) return fromInferred;
  return parseSequenceOrderFromTeId(msg.te_id);
}

export function findSequenceForFeedbackMsg(msg, sequences, videoIndex = null) {
  if (!sequences?.length) return null;

  const seqNum = resolveFeedbackSequenceOrder(msg);
  if (seqNum) {
    const byNumber = sequences.find(
      (seq) => Number(seq.sequence_order) === Number(seqNum)
    );
    if (byNumber) return byNumber;
  }

  if (sequences.length === 1) return sequences[0];

  if (videoIndex != null && videoIndex >= 0) {
    const withAssignments = sequences.filter(
      (seq) =>
        seq.player_a_id ||
        seq.player_b_id ||
        seq.player_c_id ||
        seq.player_d_id
    );
    if (withAssignments[videoIndex]) return withAssignments[videoIndex];
    if (sequences[videoIndex]) return sequences[videoIndex];
  }

  return null;
}

function isActiveRole(role) {
  if (!role) return false;
  const r = String(role).toLowerCase();
  if (r === "both") return true;
  return (
    r === "active" ||
    r === "performance" ||
    r.includes("actif") ||
    r.includes("activo") ||
    r.includes("aktiv") ||
    r.includes("perform")
  );
}

function isActiveOrSparringRole(role) {
  if (!role) return false;
  const r = String(role).toLowerCase();
  if (r === "both") return true;
  return (
    isActiveRole(role) ||
    r === "sparring" ||
    r.includes("spar")
  );
}

/** Both sides active on the same sequence — one shared evaluation clip covers both players. */
export function isDualActiveSequence(seq) {
  if (!seq) return false;
  return isActiveRole(seq.player_a_role) && isActiveRole(seq.player_b_role);
}

export function resolveSequenceRoleForPlayer(seq, ids) {
  if (!seq || !ids?.length) return null;
  const match = (value) => value != null && ids.includes(String(value));
  if (match(seq.player_a_id)) return seq.player_a_role;
  if (match(seq.player_b_id)) return seq.player_b_role;
  if (match(seq.player_c_id)) return seq.player_c_role;
  if (match(seq.player_d_id)) return seq.player_d_role;
  return null;
}

function playerLikeMatchesIds(playerLike, ids) {
  if (!playerLike || !ids?.length) return false;
  return ids.some(
    (id) =>
      (playerLike.player_id != null && String(playerLike.player_id) === id) ||
      (playerLike.user_id != null && String(playerLike.user_id) === id)
  );
}

/** Roster row (tbl_te_player) for this viewer on a multi-table TE. */
export function resolveRosterPlayerForViewer(exerciseContext, viewerIds) {
  const roster = Array.isArray(exerciseContext?.players)
    ? exerciseContext.players
    : [];
  return roster.find((p) => playerLikeMatchesIds(p, viewerIds)) || null;
}

/**
 * Multi-table warmups / table exercises often keep stale player_a/b on the
 * sequence row (last table assigned). Map Player1→A / Player2→B script roles
 * from the real tbl_te_player roster instead.
 */
export function resolveRoleFromExerciseRoster(seq, viewerIds, exerciseContext) {
  if (!seq || !viewerIds?.length) return null;
  const row = resolveRosterPlayerForViewer(exerciseContext, viewerIds);
  if (!row) return null;
  const pos = String(row.player_position || "")
    .replace(/\s+/g, "")
    .toLowerCase();
  if (pos === "player1" || pos === "a") return seq.player_a_role || "active";
  if (pos === "player2" || pos === "b") return seq.player_b_role || "sparring";
  if (pos === "player3" || pos === "c") return seq.player_c_role || null;
  if (pos === "player4" || pos === "d") return seq.player_d_role || null;
  return null;
}

/** Sequence player ids first, then multi-table roster position → script role. */
export function resolveSequenceRoleForViewer(seq, viewerIds, exerciseContext) {
  return (
    resolveSequenceRoleForPlayer(seq, viewerIds) ||
    resolveRoleFromExerciseRoster(seq, viewerIds, exerciseContext)
  );
}

/** Same-table partner from the exercise roster (Player1 ↔ Player2). */
export function resolveRosterOpponentForViewer(exerciseContext, viewerIds) {
  const row = resolveRosterPlayerForViewer(exerciseContext, viewerIds);
  if (!row) return null;
  const pos = String(row.player_position || "")
    .replace(/\s+/g, "")
    .toLowerCase();
  let want = null;
  if (pos === "player1" || pos === "a") want = "player2";
  else if (pos === "player2" || pos === "b") want = "player1";
  if (!want) return null;
  const table = Number(row.table_number);
  const roster = Array.isArray(exerciseContext?.players)
    ? exerciseContext.players
    : [];
  return (
    roster.find((p) => {
      const pPos = String(p.player_position || "")
        .replace(/\s+/g, "")
        .toLowerCase();
      if (pPos !== want) return false;
      if (Number.isFinite(table) && table > 0) {
        return Number(p.table_number) === table;
      }
      return true;
    }) || null
  );
}

export function feedbackMatchesViewer(msg, matchIds) {
  if (!matchIds?.length) return true;
  const sender = msg.sender_id != null ? String(msg.sender_id) : null;
  const receiver = msg.receiver_id != null ? String(msg.receiver_id) : null;
  return matchIds.some((id) => id === sender || id === receiver);
}

/** Coach move can share one clip with multiple actifs via view_setup.shared_user_ids. */
export function parseSharedEvaluationUserIds(fb) {
  const setup = parseFeedbackViewSetup(fb);
  const ids = setup?.shared_user_ids;
  if (!Array.isArray(ids)) return [];
  return ids.map((id) => String(id)).filter(Boolean);
}

/**
 * True when the viewer has Active/Sparring (or Both) on any sequence, or on
 * the exercise roster / player1–2 slots when there is no sequence roster.
 */
export function viewerHasActiveOrSparringParticipation(
  sequences = [],
  viewerIds = [],
  exerciseContext = null
) {
  if (!viewerIds?.length) return false;

  for (const seq of sequences || []) {
    const role = resolveSequenceRoleForViewer(seq, viewerIds, exerciseContext);
    if (role && isActiveOrSparringRole(role)) return true;
  }

  if (sequences?.length) {
    // Multi-table: sequence ids may be stale — roster membership still counts
    // for exercise-level participation when no per-sequence role resolved.
    const roster = Array.isArray(exerciseContext?.players)
      ? exerciseContext.players
      : [];
    if (roster.some((p) => playerLikeMatchesIds(p, viewerIds))) return true;
    return false;
  }

  if (!exerciseContext) return true;

  const roster = Array.isArray(exerciseContext.players)
    ? exerciseContext.players
    : [];
  if (roster.some((p) => playerLikeMatchesIds(p, viewerIds))) return true;

  return [exerciseContext.player1, exerciseContext.player2].some((slot) =>
    playerLikeMatchesIds(slot, viewerIds)
  );
}

/**
 * Match evaluation clips to a viewer.
 * Viewer must have Active or Sparring on the clip's sequence (same-table footage).
 * When the clip lists filmed tables (wide-camera calibration) or a filename /
 * Move table, the viewer must be on one of those tables — otherwise changing
 * only the table would leave the video on the previous player's Media list.
 * Coach-moved dual-active clips also match view_setup.shared_user_ids.
 * TE_ONCE clips (no sequences) require exercise-level Active/Sparring participation.
 * Being the uploader (sender_id) alone is not enough.
 */
export function evaluationFeedbackMatchesViewer(
  msg,
  viewerIds,
  sequences = [],
  { targetSequenceOrder = null, exerciseContext = null } = {}
) {
  if (!msg?.video_message_url || msg.message_type !== "evaluation") return false;
  if (!viewerIds?.length) return true;

  const msgSeq = resolveFeedbackSequenceOrder(msg);
  if (targetSequenceOrder != null) {
    if (msgSeq != null && Number(msgSeq) !== Number(targetSequenceOrder)) {
      return false;
    }
  }

  const sharedIds = parseSharedEvaluationUserIds(msg);
  if (sharedIds.some((id) => viewerIds.includes(id))) return true;
  const assignedIds = manualAssignedIdentityIds(msg);
  if (assignedIds.some((id) => viewerIds.includes(id))) return true;

  const filmedTables = clipFilmedTableNumbers(msg);
  if (
    filmedTables.length &&
    exerciseContext &&
    !filmedTables.some((table) =>
      viewerIsOnExerciseTable(viewerIds, table, exerciseContext)
    )
  ) {
    return false;
  }

  // TE_ONCE / timer exercises: one exercise-level clip, not tied to a sequence roster.
  if (!sequences?.length && targetSequenceOrder == null) {
    return viewerHasActiveOrSparringParticipation(
      sequences,
      viewerIds,
      exerciseContext
    );
  }

  const seq = findSequenceForFeedbackMsg(msg, sequences);
  if (seq) {
    const role = resolveSequenceRoleForViewer(seq, viewerIds, exerciseContext);
    if (role && isActiveOrSparringRole(role)) return true;
  }

  return false;
}

export function filterEvaluationVideosForViewer(
  feedbacks,
  viewerIds,
  sequences = [],
  options = {}
) {
  if (!Array.isArray(feedbacks)) return [];
  const preferredTeId = options.preferredTeId;
  const exerciseContext = options.exerciseContext || null;
  const matched = feedbacks.filter((fb) => {
    if (fb.message_type !== "evaluation" || !fb.video_message_url) return false;
    if (
      preferredTeId &&
      !feedbackVideoBelongsToTe(fb, preferredTeId)
    ) {
      return false;
    }
    // Uploader always keeps their own clip, even if sequence_number drifted
    // (live session advanced before upload finished).
    const senderId = String(fb.sender_id ?? fb.sender?.user_id ?? "").trim();
    const isUploader =
      Boolean(senderId) &&
      Array.isArray(viewerIds) &&
      viewerIds.some((id) => String(id) === senderId);

    if (
      !isUploader &&
      !isManualTeAssignment(fb) &&
      !feedbackSequencePlausibleForExercise(fb, sequences)
    ) {
      return false;
    }
    if (
      evaluationFeedbackMatchesViewer(fb, viewerIds, sequences, {
        exerciseContext,
      })
    ) {
      return true;
    }
    if (isUploader) return true;

    // Missing-sequence mobile clips still belong to the exercise.
    if (resolveFeedbackSequenceOrder(fb) == null) {
      return viewerHasActiveOrSparringParticipation(
        sequences,
        viewerIds,
        exerciseContext
      );
    }
    return false;
  });
  return dedupeEvaluationVideosByUrl(matched, options);
}

export function groupEvaluationVideosBySequence(videos, sequences = []) {
  const sortedSeqs = [...sequences].sort(
    (a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0)
  );

  const buckets = sortedSeqs.map((seq, idx) => ({
    seq,
    seqOrder: seq.sequence_order ?? idx + 1,
    videos: [],
  }));

  const unmatched = [];

  for (const msg of videos || []) {
    const matchedSeq = findSequenceForFeedbackMsg(msg, sequences);
    const order = matchedSeq?.sequence_order ?? resolveFeedbackSequenceOrder(msg);
    const bucket = buckets.find((b) => Number(b.seqOrder) === Number(order));
    if (bucket) {
      bucket.videos.push(msg);
    } else {
      unmatched.push(msg);
    }
  }

  return { buckets, unmatched };
}

/**
 * Coverage report: expected sequences where viewer participates vs videos found.
 */
export function buildEvaluationVideoCoverage(sequences, videos, viewerIds, options = {}) {
  // Distinct physical clips only — do not collapse different URLs on dual-active
  // sequences (that hid coach-moved videos under the oldest clip).
  const uniqueVideos = dedupeEvaluationVideosByUrl(videos, options);
  const exerciseContext = options.exerciseContext || null;

  const sortedSeqs = [...sequences].sort(
    (a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0)
  );

  const participating = sortedSeqs.filter((seq) => {
    const role = resolveSequenceRoleForViewer(seq, viewerIds, exerciseContext);
    return role && isActiveOrSparringRole(role);
  });

  const { buckets } = groupEvaluationVideosBySequence(uniqueVideos, sequences);

  const rows = participating.map((seq) => {
    const seqOrder = seq.sequence_order;
    const bucket = buckets.find((b) => Number(b.seqOrder) === Number(seqOrder));
    const matching = (bucket?.videos || []).filter((msg) =>
      evaluationFeedbackMatchesViewer(msg, viewerIds, sequences, {
        targetSequenceOrder: seqOrder,
        exerciseContext,
      })
    );
    return {
      seq,
      seqOrder,
      expected: true,
      videoCount: matching.length,
      hasVideo: matching.length > 0,
      videos: matching,
    };
  });

  const expectedCount = rows.length;
  const foundCount = rows.filter((r) => r.hasVideo).length;
  const extraVideos = uniqueVideos.filter((msg) => {
    const seq = findSequenceForFeedbackMsg(msg, sequences);
    if (!seq) return true;
    const role = resolveSequenceRoleForViewer(seq, viewerIds, exerciseContext);
    return !role || !isActiveOrSparringRole(role);
  });

  return {
    expectedCount,
    foundCount,
    complete: expectedCount > 0 && foundCount >= expectedCount,
    rows,
    extraVideoCount: extraVideos.length,
    totalVideoCount: uniqueVideos.length,
  };
}
